/**
 * 評価点の説明ユーティリティ
 *
 * 仕様書 8.3 定性的行動評価の10段階定義に基づく
 */

export interface ScoreDescription {
  score: number;
  description: string;
  isStandard: boolean; // 基準点かどうか（6点）
}

/**
 * 評価点の説明を取得
 * @param score 評価点（1-10）
 * @returns 評価点の説明情報
 */
export function getScoreDescription(score: number | null | undefined): ScoreDescription {
  // スコアが存在しない場合のデフォルト
  if (score === null || score === undefined) {
    return {
      score: 0,
      description: '未評価',
      isStandard: false
    };
  }

  // 1-10の範囲外の場合は最も近い値に丸める
  const normalizedScore = Math.max(1, Math.min(10, Math.round(score)));

  const descriptions: Record<number, string> = {
    10: '期待を大幅に超え、他の模範となる極めて顕著な行動・成果を継続的に示した',
    9: '期待を大幅に超える顕著な行動・成果を示した',
    8: '期待を十分に超える優れた行動・成果を示した',
    7: '期待を超える良好な行動・成果を示した',
    6: '期待された役割・目標を概ね達成し、標準的な行動・成果を示した（基準）',
    5: '期待された役割・目標を部分的に達成したが、一部課題が残る',
    4: '期待された役割・目標に対し、未達成な点が多く、改善努力が必要',
    3: '期待された役割・目標に対し、著しく未達成であり、大幅な改善と指導が必要',
    2: '期待された役割・目標に対し、ほとんど達成できず、業務遂行に支障があるレベル',
    1: '期待された役割・目標に対し、全く達成できず、職務遂行能力に重大な課題がある'
  };

  return {
    score: normalizedScore,
    description: descriptions[normalizedScore] || '評価基準外',
    isStandard: normalizedScore === 6
  };
}

/**
 * 評価点の短い説明を取得（カード表示用）
 * @param score 評価点（1-10）
 * @returns 短い説明
 */
export function getShortScoreDescription(score: number | null | undefined): string {
  if (score === null || score === undefined) {
    return '未評価';
  }

  const normalizedScore = Math.max(1, Math.min(10, Math.round(score)));

  const shortDescriptions: Record<number, string> = {
    10: '極めて顕著',
    9: '顕著',
    8: '優れた成果',
    7: '良好',
    6: '標準（基準）',
    5: '一部課題あり',
    4: '改善努力が必要',
    3: '大幅な改善が必要',
    2: '業務遂行に支障',
    1: '重大な課題'
  };

  return shortDescriptions[normalizedScore] || '評価基準外';
}

/**
 * 評価点の色を取得（UI表示用）
 * @param score 評価点（1-10）
 * @returns MUIの色名
 */
export function getScoreColor(score: number | null | undefined): 'error' | 'warning' | 'info' | 'success' | 'default' {
  if (score === null || score === undefined) {
    return 'default';
  }

  const normalizedScore = Math.max(1, Math.min(10, Math.round(score)));

  if (normalizedScore >= 9) return 'success';
  if (normalizedScore >= 7) return 'info';
  if (normalizedScore >= 5) return 'warning';
  return 'error';
}
